import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';

const s = {
  page: { fontFamily: 'system-ui, -apple-system, sans-serif', background: '#f8fafc' },
  hero: {
    background: '#080d1a',
    padding: '48px 24px 64px',
    textAlign: 'center',
  },
  heroLabel: {
    display: 'inline-block',
    background: 'rgba(99,102,241,0.15)',
    color: '#818cf8',
    fontSize: 13,
    fontWeight: 600,
    padding: '5px 14px',
    borderRadius: 20,
    marginBottom: 20,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 38,
    fontWeight: 800,
    color: '#f8fafc',
    marginBottom: 16,
    letterSpacing: '-0.8px',
    lineHeight: 1.2,
    maxWidth: 700,
    margin: '0 auto 16px',
  },
  heroMeta: {
    fontSize: 14,
    color: '#64748b',
    marginTop: 16,
  },
  body: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '56px 24px 80px',
  },
  h2: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    marginTop: 44,
    marginBottom: 14,
  },
  mistakeNum: {
    display: 'inline-block',
    background: '#fef2f2',
    color: '#dc2626',
    fontWeight: 700,
    fontSize: 13,
    padding: '3px 10px',
    borderRadius: 20,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  p: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 1.75,
    marginBottom: 20,
  },
  ul: {
    paddingLeft: 20,
    marginBottom: 20,
  },
  li: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 1.75,
    marginBottom: 8,
  },
  fixBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderLeft: '4px solid #16a34a',
    borderRadius: 8,
    padding: '14px 18px',
    marginBottom: 28,
    fontSize: 15,
    color: '#15803d',
    lineHeight: 1.65,
  },
  divider: {
    borderTop: '1px solid #e2e8f0',
    margin: '36px 0',
  },
  cta: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    borderRadius: 14,
    padding: '36px 32px',
    marginTop: 52,
    textAlign: 'center',
  },
  ctaTitle: { fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 10 },
  ctaText: { fontSize: 15, color: 'rgba(255,255,255,0.8)', marginBottom: 24, lineHeight: 1.6 },
  ctaBtn: {
    display: 'inline-block',
    background: '#fff', color: '#1e40af', fontWeight: 700,
    fontSize: 15, padding: '12px 28px', borderRadius: 8, textDecoration: 'none',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    color: '#64748b',
    textDecoration: 'none',
    marginBottom: 32,
  },
};

export default function MistakesFirstBid() {
  return (
    <div style={s.page}>

      <div style={s.hero}>
        <div style={s.heroLabel}>Best practice</div>
        <h1 style={s.heroTitle}>5 mistakes to avoid when bidding on your first EU contract</h1>
        <div style={s.heroMeta}>March 2026 &nbsp;·&nbsp; 7 min read</div>
      </div>

      <div style={s.body}>
        <Link to="/blog" style={s.backLink}>← Back to blog</Link>

        <p style={s.p}>
          Winning your first EU public contract is a meaningful milestone — it opens the door to a market worth trillions annually and can provide stable, long-term revenue. But the procurement process has its own rules, conventions, and pitfalls that catch out even experienced businesses on their first attempt.
        </p>
        <p style={s.p}>
          We have reviewed hundreds of first-time bid submissions and spoken with procurement officers across several EU member states. These are the five mistakes we see most often — and how to avoid them.
        </p>

        <div style={s.divider} />

        <div style={s.mistakeNum}>Mistake 1</div>
        <h2 style={{ ...s.h2, marginTop: 8 }}>Bidding on contracts you have no realistic chance of winning</h2>
        <p style={s.p}>
          The most common mistake first-time bidders make is not being selective enough. Procurement processes are time-consuming: a single bid can take 20–80 hours to prepare properly. If you bid on every vaguely relevant contract, you will spread your team thin and win very little.
        </p>
        <p style={s.p}>
          Before starting any bid, apply a quick scoring test. Ask whether you can evidence at least three similar contracts in your portfolio, whether you meet all mandatory requirements, and whether the contract size is proportionate to your turnover (most contracting authorities require annual turnover of at least twice the contract value).
        </p>
        <div style={s.fixBox}>
          <strong>Fix:</strong> Bid only on contracts where you score at least 70% on your own pre-qualification assessment. A focused 3 bids with strong cases beats 10 weak submissions every time.
        </div>

        <div style={s.divider} />

        <div style={s.mistakeNum}>Mistake 2</div>
        <h2 style={{ ...s.h2, marginTop: 8 }}>Reading the specification too quickly</h2>
        <p style={s.p}>
          Procurement documents are long and dense. It is tempting to skim them. But buried in the specification are mandatory requirements, specific formatting rules, and evaluation criteria that determine whether your bid will even be assessed — let alone score well.
        </p>
        <p style={s.p}>
          A missing mandatory certificate, a response that exceeds the page limit, or a signature in the wrong place can result in automatic disqualification. Procurement officers are legally obliged to reject non-compliant bids regardless of their content quality.
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Read every section of the specification, including appendices and conditions</li>
          <li style={s.li}>Create a compliance checklist: mandatory requirements, documents required, formatting rules</li>
          <li style={s.li}>Highlight the evaluation criteria and their weightings — your bid structure should mirror them</li>
          <li style={s.li}>Note the deadline for clarification questions (usually 10–15 days before submission) and use it</li>
        </ul>
        <div style={s.fixBox}>
          <strong>Fix:</strong> Allocate at least 20% of your bid preparation time purely to reading and annotating the specification before you write a single word of your response.
        </div>

        <div style={s.divider} />

        <div style={s.mistakeNum}>Mistake 3</div>
        <h2 style={{ ...s.h2, marginTop: 8 }}>Writing a generic response instead of a tailored one</h2>
        <p style={s.p}>
          Procurement officers read dozens of bids. Generic, copy-paste responses are immediately recognisable. Phrases like "we are a dynamic and innovative company committed to delivering excellence" communicate nothing and score nothing. Every evaluator has read those words a hundred times.
        </p>
        <p style={s.p}>
          The highest-scoring bids are the ones that directly address the contracting authority's specific situation, constraints, and objectives — and back up every claim with concrete evidence from similar contracts.
        </p>
        <p style={s.p}>
          Structure every response around: understanding of the requirement, your proposed approach, evidence that you have done this before, and measurable outcomes you delivered. Avoid adjectives. Use numbers.
        </p>
        <div style={s.fixBox}>
          <strong>Fix:</strong> For every quality question, reference the contracting authority by name and their specific context at least once. Include one concrete case study from a similar contract with measurable outcomes (e.g., delivered 15% under budget, reduced processing time by 30%).
        </div>

        <div style={s.divider} />

        <div style={s.mistakeNum}>Mistake 4</div>
        <h2 style={{ ...s.h2, marginTop: 8 }}>Underpricing or pricing without a strategy</h2>
        <p style={s.p}>
          Most first-time bidders either underprice significantly (thinking the lowest price wins) or have no pricing rationale at all. Both are mistakes.
        </p>
        <p style={s.p}>
          EU procurement uses <strong>MEAT</strong> (Most Economically Advantageous Tender) evaluation in the majority of contracts. Price is typically only one of several weighted criteria, often accounting for 30–40% of the total score. Quality, methodology, experience, and sustainability frequently outweigh price. An unrealistically low price raises red flags about delivery capability and can actually harm your overall score.
        </p>
        <p style={s.p}>
          On the other hand, pricing without research is also risky. Before submitting, research the contract's budget (often disclosed in the specification or estimable from award notices of similar contracts), understand what the market rate is, and price to deliver profitably — not to win at any cost.
        </p>
        <div style={s.fixBox}>
          <strong>Fix:</strong> Use contract award notices on TED to research what similar contracts have been awarded for. Price competitively but realistically. Write a pricing narrative that justifies your costs — "value for money" is evaluated, not just the number.
        </div>

        <div style={s.divider} />

        <div style={s.mistakeNum}>Mistake 5</div>
        <h2 style={{ ...s.h2, marginTop: 8 }}>Submitting at the last minute</h2>
        <p style={s.p}>
          Public procurement portals have hard deadlines. Most online submission systems close automatically at the stated time — a submission that arrives one minute late will be rejected, regardless of circumstance. And procurement portals are notorious for slow upload speeds and technical issues close to deadlines.
        </p>
        <p style={s.p}>
          Beyond the technical risk, last-minute submissions almost always contain errors. Rushed bids have missing attachments, inconsistent pricing, or responses that didn't fully answer the question. The time pressure means nobody does a final check.
        </p>
        <ul style={s.ul}>
          <li style={s.li}>Aim to have your complete submission ready at least 48 hours before the deadline</li>
          <li style={s.li}>Do a final compliance check against your checklist before submitting</li>
          <li style={s.li}>Submit a test or draft through the portal early if the system allows it</li>
          <li style={s.li}>Keep a PDF copy of everything you submitted</li>
        </ul>
        <div style={s.fixBox}>
          <strong>Fix:</strong> Set an internal deadline of 48 hours before the official one. Treat it as the real deadline. Use the last 48 hours for review, not writing.
        </div>

        <div style={s.divider} />

        <h2 style={s.h2}>One final note: request feedback after every bid</h2>
        <p style={s.p}>
          EU procurement law gives unsuccessful bidders the right to request a debrief — a written or verbal explanation of why they didn't win and how their bid scored compared to the winner. Most businesses never request this, leaving valuable intelligence on the table.
        </p>
        <p style={s.p}>
          Even if your first bid is unsuccessful, a good debrief gives you exactly what you need to improve. Over time, systematic debriefing is one of the most effective ways to increase your win rate.
        </p>

        <div style={s.cta}>
          <div style={s.ctaTitle}>Find the right tenders to bid on</div>
          <div style={s.ctaText}>
            The first step to avoiding all five mistakes is making sure you only bid on contracts you can genuinely win. Jyto ranks EU tenders by relevance to your company so you spend time on the right opportunities.
          </div>
          <Link to="/signup" style={s.ctaBtn}>Try Jyto free</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
